import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { fetchFactoryClients, FactoryClientData } from '../../manage_clients/factory_clients/service/factoryClientService';
import { fetchIndividualClients, IndividualClientData } from '../../manage_clients/individual_clients/service/individualClientService';
import { fetchBankClients, BankClientData } from '../../manage_clients/bank_clients/service/bankClientService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const Badge = styled.span<{ variant?: 'individual' | 'factory' | 'bank' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: capitalize;
  background-color: ${({ variant }) => {
    switch (variant) {
      case 'individual':
        return theme.colors.primary[100];
      case 'factory':
        return theme.colors.accent.warning + '20';
      case 'bank':
        return theme.colors.accent.info + '20';
      default:
        return theme.colors.light.background.tertiary;
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'individual':
        return theme.colors.primary[700];
      case 'factory':
        return theme.colors.accent.warning;
      case 'bank':
        return theme.colors.accent.info;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

interface ClientData {
  id: string;
  name: string;
  type: 'individual' | 'factory' | 'bank';
  mobile?: string;
  address?: string;
  industryName?: string;
  bankName?: string;
  branchCode?: string;
  industryId?: string;
  bankId?: string;
  branchId?: string;
}

const DeploymentPage: React.FC = () => {
  const { user, companyData } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const expectedCompanyId = '';
  const companyId = (companyData?.id === expectedCompanyId) ? companyData.id : expectedCompanyId;

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, companyData]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all client types
      const [factoryClients, individualClients, bankClients] = await Promise.all([
        fetchFactoryClients(companyId).catch(() => []),
        fetchIndividualClients(companyId).catch(() => []),
        fetchBankClients(companyId).catch(() => []),
      ]);

      const allClients: ClientData[] = [
        ...factoryClients.map((c) => ({
          id: c.id,
          name: c.name,
          type: 'factory' as const,
          mobile: c.mobile,
          address: c.address,
          industryName: c.industryName,
          industryId: c.industryId,
        })),
        ...individualClients.map((c) => ({
          id: c.id,
          name: c.name,
          type: 'individual' as const,
          mobile: c.mobile,
          address: c.address,
        })),
        ...bankClients.map((c) => ({
          id: c.branchId,
          name: c.bankName,
          type: 'bank' as const,
          mobile: c.mobile,
          address: c.address,
          bankName: c.bankName,
          branchCode: c.branchCode,
          bankId: c.bankId,
          branchId: c.branchId,
        })),
      ];

      setClients(allClients);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter((c) => {
      const name = c.name?.toLowerCase() || '';
      const mobile = c.mobile?.toLowerCase() || '';
      const address = c.address?.toLowerCase() || '';
      const industryName = c.industryName?.toLowerCase() || '';
      const bankName = c.bankName?.toLowerCase() || '';
      const branchCode = c.branchCode?.toLowerCase() || '';
      return (
        name.includes(term) ||
        mobile.includes(term) ||
        address.includes(term) ||
        industryName.includes(term) ||
        bankName.includes(term) ||
        branchCode.includes(term)
      );
    });
  }, [clients, searchTerm]);

  const handleViewDeployment = (type: 'individual' | 'factory' | 'bank') => {
    if (type === 'individual') {
      navigate('/deployment/individual');
    } else if (type === 'factory') {
      navigate('/deployment/factory');
    } else if (type === 'bank') {
      navigate('/deployment/bank');
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Deployment</PageTitle>
      </PageHeader>

      <Card>
        <CardBody>
          <SearchContainer>
            <SearchInputContainer>
              <Input
                placeholder="Search by name, mobile, address, industry, or bank..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
              />
            </SearchInputContainer>
          </SearchContainer>

          {loading ? (
            <EmptyState>
              <EmptyStateText>Loading...</EmptyStateText>
            </EmptyState>
          ) : filteredClients.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Client Name</Table.HeaderCell>
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Mobile</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredClients.map((client) => (
                  <Table.Row key={`${client.type}-${client.id}`}>
                    <Table.Cell>
                      {client.type === 'bank' && client.branchCode
                        ? `${client.name} - ${client.branchCode}`
                        : client.type === 'factory' && client.industryName
                        ? `${client.industryName} - ${client.name}`
                        : client.name || '—'}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={client.type}>{client.type}</Badge>
                    </Table.Cell>
                    <Table.Cell>{client.mobile || '—'}</Table.Cell>
                    <Table.Cell>{client.address || '—'}</Table.Cell>
                    <Table.Cell>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleViewDeployment(client.type)}
                      >
                        View Deployment
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <EmptyStateText>No clients found.</EmptyStateText>
            </EmptyState>
          )}
        </CardBody>
      </Card>
    </DashboardLayout>
  );
};

export default DeploymentPage;
